import Footer from "@/components/layout/Footer";
import Header from "@/components/layout/Header";
import appConfig from "@config";
import {
  AlertCircle,
  CheckCircle2,
  Clock,
  Mail,
  MapPin,
  MessageSquare,
  Phone,
  Send,
} from "lucide-react";
import { useState } from "react";

interface FormErrors {
  name?: string;
  email?: string;
  message?: string;
}

export default function PageContact() {
  const [result, setResult] = useState<string>("");
  const [status, setStatus] = useState<
    "idle" | "loading" | "success" | "error"
  >("idle");
  const [errors, setErrors] = useState<FormErrors>({});

  const validate = (formData: FormData): FormErrors => {
    const errs: FormErrors = {};
    const name = String(formData.get("name") || "").trim();
    const email = String(formData.get("email") || "").trim();
    const message = String(formData.get("message") || "").trim();

    if (!name) {
      errs.name = "Veuillez renseigner votre nom complet.";
    } else if (name.length < 2) {
      errs.name = "Le nom doit contenir au moins 2 caractères.";
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email) {
      errs.email = "Veuillez renseigner votre adresse courriel.";
    } else if (!emailRegex.test(email)) {
      errs.email =
        "Veuillez entrer une adresse courriel valide (ex: nom@domaine.com).";
    }

    if (!message) {
      errs.message = "Veuillez rédiger votre message.";
    } else if (message.length < 10) {
      errs.message = "Votre message doit comporter au moins 10 caractères.";
    }

    return errs;
  };

  const clearFieldError = (field: keyof FormErrors) => {
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    // Validation JavaScript côté client
    const validationErrors = validate(formData);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setStatus("error");
      setResult(
        "Veuillez corriger les champs requis indiqués en rouge ci-dessous.",
      );
      return;
    }

    setErrors({});
    setStatus("loading");
    setResult("Envoi de votre message en cours...");
    formData.append("access_key", appConfig.WEBFORMS3_EMAIL_KEY);
    formData.append("from_name", "Site Web Église Garizim");

    try {
      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        body: formData,
      });

      const data = await response.json();

      if (data.success) {
        setStatus("success");
        setResult(
          "Merci ! Votre message a été envoyé avec succès. Nous vous répondrons sous peu.",
        );
        form.reset();
        setErrors({});
      } else {
        setStatus("error");
        setResult(
          data.message ||
            "Une erreur est survenue lors de l'envoi. Veuillez vérifier vos informations ou nous écrire à egarizim@gmail.com.",
        );
      }
    } catch (error) {
      console.error("Erreur Web3Forms:", error);
      setStatus("error");
      setResult(
        "Impossible de joindre le serveur d'envoi. Veuillez nous contacter directement à egarizim@gmail.com.",
      );
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 font-sans flex flex-col justify-between">
      <Header />

      <main className="flex-1 pt-20 pb-12 sm:pt-24 sm:pb-20">
        {/* Hero Section */}
        <section className="bg-slate-950 text-white py-8 sm:py-14 sm:py-20 relative overflow-hidden border-b border-slate-800">
          <div className="absolute -top-24 -right-24 w-96 h-96 bg-amber-400/10 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-sky-500/10 rounded-full blur-3xl pointer-events-none" />

          <div className="container mx-auto px-4 relative z-10 text-center max-w-3xl">
            <span className="inline-block px-3 py-1 rounded-full bg-white/10 text-church-gold-400 text-[10px] sm:text-sm font-semibold tracking-wider uppercase mb-2 sm:mb-4 border border-white/10">
              Église de Dieu Garizim
            </span>
            <h1 className="text-2xl sm:text-5xl font-extrabold text-white mb-2 sm:mb-4 tracking-tight">
              Contactez-nous
            </h1>
            <p className="text-gray-300 text-xs sm:text-lg leading-relaxed">
              Nous sommes à votre disposition pour toute question, requête de
              prière ou information concernant notre assemblée et nos services.
            </p>
          </div>
        </section>

        {/* Content Section */}
        <section className="container mx-auto px-4 -mt-4 sm:-mt-8 relative z-20 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 sm:gap-8">
            {/* Info Cards Column */}
            <div className="lg:col-span-5 space-y-3 sm:space-y-4">
              <div className="bg-slate-900/80 backdrop-blur-md rounded-2xl p-4 sm:p-6 shadow-xl border border-slate-700/60">
                <h2 className="text-base sm:text-xl font-bold text-white mb-3 sm:mb-6 flex items-center gap-2">
                  <MessageSquare className="w-4 h-4 sm:w-5 sm:h-5 text-church-gold-500" />
                  <span>Nos Coordonnées</span>
                </h2>

                <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-1 gap-3 sm:gap-6">
                  {/* Adresse */}
                  <div className="flex items-start gap-2.5 sm:gap-4">
                    <div className="p-2 sm:p-3 rounded-xl bg-church-blue-50 text-church-blue-800 flex-shrink-0">
                      <MapPin className="w-4 h-4 sm:w-5 sm:h-5" />
                    </div>
                    <div>
                      <h3 className="text-[10px] sm:text-xs font-bold uppercase tracking-wider text-slate-400">
                        Adresse
                      </h3>
                      <p className="text-slate-200 font-medium text-xs sm:text-sm mt-0.5 leading-snug">
                        11800 4e Avenue, Montréal, QC H1E 5Y2
                      </p>
                      <a
                        href="https://maps.google.com/?q=11800+4e+Avenue,+Montréal,+QC+H1E+5Y2"
                        target="_blank"
                        rel="noreferrer"
                        className="text-xs text-sky-400 hover:text-sky-300 font-semibold underline mt-0.5 inline-block"
                      >
                        Voir sur Google Maps →
                      </a>
                    </div>
                  </div>

                  {/* Téléphone */}
                  <div className="flex items-start gap-2.5 sm:gap-4">
                    <div className="p-2 sm:p-3 rounded-xl bg-church-gold-50 text-church-gold-700 flex-shrink-0">
                      <Phone className="w-4 h-4 sm:w-5 sm:h-5" />
                    </div>
                    <div>
                      <h3 className="text-[10px] sm:text-xs font-bold uppercase tracking-wider text-slate-400">
                        Téléphone
                      </h3>
                      <a
                        href="tel:5143224921"
                        className="text-slate-200 font-semibold text-xs sm:text-sm hover:text-amber-400 transition-colors mt-0.5 block"
                      >
                        (514) 322-4921
                      </a>
                    </div>
                  </div>

                  {/* Courriel */}
                  <div className="flex items-start gap-2.5 sm:gap-4">
                    <div className="p-2 sm:p-3 rounded-xl bg-sky-50 text-sky-700 flex-shrink-0">
                      <Mail className="w-4 h-4 sm:w-5 sm:h-5" />
                    </div>
                    <div>
                      <h3 className="text-[10px] sm:text-xs font-bold uppercase tracking-wider text-slate-400">
                        Courriel
                      </h3>
                      <a
                        href="mailto:egarizim@gmail.com"
                        className="text-slate-200 font-semibold text-xs sm:text-sm hover:text-sky-400 transition-colors mt-0.5 block"
                      >
                        egarizim@gmail.com
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              {/* Horaires Card */}
              <div className="bg-slate-900/80 backdrop-blur-md rounded-2xl p-4 sm:p-6 shadow-xl border border-slate-700/60">
                <h2 className="text-sm sm:text-base font-bold text-white mb-2.5 sm:mb-4 flex items-center gap-2">
                  <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-church-gold-500" />
                  <span>Horaires des Rencontres</span>
                </h2>
                <ul className="space-y-2 sm:space-y-3 text-xs sm:text-sm text-slate-400">
                  <li className="flex justify-between border-b border-slate-700/60 pb-1.5 sm:pb-2">
                    <span className="font-semibold text-slate-200">
                      Culte d'adoration
                    </span>
                    <span className="text-amber-400 font-bold">
                      Dimanche 10h00
                    </span>
                  </li>
                  <li className="flex justify-between border-b border-slate-700/60 pb-1.5 sm:pb-2">
                    <span className="font-semibold text-slate-200">
                      Étude Biblique & Prière
                    </span>
                    <span className="text-amber-400 font-bold">
                      Mardi 18h30
                    </span>
                  </li>
                  <li className="flex justify-between">
                    <span className="font-semibold text-slate-200">
                      Jeunesse
                    </span>
                    <span className="text-amber-400 font-bold">
                      Samedi 9h00
                    </span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Form Column */}
            <div className="lg:col-span-7">
              <div className="bg-slate-900/80 backdrop-blur-md rounded-2xl p-4 sm:p-8 shadow-xl border border-slate-700/60">
                <div className="mb-4 sm:mb-6">
                  <h2 className="text-xl sm:text-2xl font-bold text-white">
                    Envoyez-nous un message
                  </h2>
                  <p className="text-xs sm:text-sm text-slate-400 mt-1">
                    Remplissez ce formulaire et notre équipe vous répondra dans
                    les plus brefs délais.
                  </p>
                </div>

                <form
                  onSubmit={onSubmit}
                  className="space-y-3.5 sm:space-y-5"
                  noValidate
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    {/* Nom */}
                    <div>
                      <label
                        htmlFor="contact-name"
                        className="block text-[11px] sm:text-xs font-bold uppercase tracking-wider text-slate-300 mb-1"
                      >
                        Nom complet <span className="text-rose-500">*</span>
                      </label>
                      <input
                        id="contact-name"
                        type="text"
                        name="name"
                        onChange={() => clearFieldError("name")}
                        placeholder="Votre nom et prénom"
                        className={`w-full px-3.5 py-2.5 sm:px-4 sm:py-3 rounded-xl border ${
                          errors.name
                            ? "border-rose-500 focus:ring-rose-500"
                            : "border-gray-200 focus:ring-church-blue-700"
                        } text-gray-800 text-xs sm:text-sm focus:outline-none focus:ring-2 focus:bg-white transition-all`}
                      />
                      {errors.name && (
                        <p className="text-xs text-rose-600 font-medium mt-1 flex items-center gap-1">
                          <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
                          <span>{errors.name}</span>
                        </p>
                      )}
                    </div>

                    {/* Email */}
                    <div>
                      <label
                        htmlFor="contact-email"
                        className="block text-[11px] sm:text-xs font-bold uppercase tracking-wider text-slate-300 mb-1"
                      >
                        Adresse courriel{" "}
                        <span className="text-rose-500">*</span>
                      </label>
                      <input
                        id="contact-email"
                        type="email"
                        name="email"
                        onChange={() => clearFieldError("email")}
                        placeholder="votre.email@exemple.com"
                        className={`w-full px-3.5 py-2.5 sm:px-4 sm:py-3 rounded-xl border ${
                          errors.email
                            ? "border-rose-500 focus:ring-rose-500"
                            : "border-gray-200 focus:ring-church-blue-700"
                        } text-gray-800 text-xs sm:text-sm focus:outline-none focus:ring-2 focus:bg-white transition-all`}
                      />
                      {errors.email && (
                        <p className="text-xs text-rose-600 font-medium mt-1 flex items-center gap-1">
                          <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
                          <span>{errors.email}</span>
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    {/* Téléphone */}
                    <div>
                      <label
                        htmlFor="contact-phone"
                        className="block text-[11px] sm:text-xs font-bold uppercase tracking-wider text-gray-300 mb-1"
                      >
                        Téléphone{" "}
                        <span className="text-gray-400 font-normal">
                          (facultatif)
                        </span>
                      </label>
                      <input
                        id="contact-phone"
                        type="tel"
                        name="phone"
                        placeholder="(514) 000-0000"
                        className="w-full px-3.5 py-2.5 sm:px-4 sm:py-3 rounded-xl border border-gray-200 text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-church-blue-700 focus:bg-white transition-all"
                      />
                    </div>

                    {/* Sujet */}
                    <div>
                      <label
                        htmlFor="contact-subject"
                        className="block text-[11px] sm:text-xs font-bold uppercase tracking-wider text-gray-300 mb-1"
                      >
                        Sujet
                      </label>
                      <input
                        id="contact-subject"
                        type="text"
                        name="subject"
                        placeholder="Raison de votre message"
                        className="w-full px-3.5 py-2.5 sm:px-4 sm:py-3 rounded-xl border border-gray-200 text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-church-blue-700 focus:bg-white transition-all"
                      />
                    </div>
                  </div>

                  {/* Message */}
                  <div>
                    <label
                      htmlFor="contact-message"
                      className="block text-[11px] sm:text-xs font-bold uppercase tracking-wider text-slate-300 mb-1"
                    >
                      Message <span className="text-rose-500">*</span>
                    </label>
                    <textarea
                      id="contact-message"
                      name="message"
                      rows={4}
                      onChange={() => clearFieldError("message")}
                      placeholder="Écrivez votre message ici..."
                      className={`w-full px-3.5 py-2.5 sm:px-4 sm:py-3 rounded-xl border ${
                        errors.message
                          ? "border-rose-500 focus:ring-rose-500"
                          : "border-gray-200 focus:ring-church-blue-700"
                      } text-gray-800 text-xs sm:text-sm focus:outline-none focus:ring-2 focus:bg-white transition-all resize-y`}
                    ></textarea>
                    {errors.message && (
                      <p className="text-xs text-rose-600 font-medium mt-1 flex items-center gap-1">
                        <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
                        <span>{errors.message}</span>
                      </p>
                    )}
                  </div>

                  {/* Status Banner */}
                  {result && (
                    <div
                      className={`p-3 sm:p-4 rounded-xl text-xs sm:text-sm flex items-start gap-2.5 ${
                        status === "success"
                          ? "bg-emerald-50 border border-emerald-200 text-emerald-800"
                          : status === "error"
                            ? "bg-rose-50 border border-rose-200 text-rose-800"
                            : "bg-blue-50 border border-blue-200 text-blue-800"
                      }`}
                    >
                      {status === "success" ? (
                        <CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                      ) : status === "error" ? (
                        <AlertCircle className="w-4 h-4 sm:w-5 sm:h-5 text-rose-600 flex-shrink-0 mt-0.5" />
                      ) : (
                        <span className="w-3.5 h-3.5 sm:w-4 sm:h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin flex-shrink-0 mt-0.5" />
                      )}
                      <span>{result}</span>
                    </div>
                  )}

                  {/* Submit Button */}
                  <div>
                    <button
                      type="submit"
                      disabled={status === "loading"}
                      className="w-full sm:w-auto px-6 sm:px-8 py-2.5 sm:py-3.5 rounded-full bg-church-blue-900 hover:bg-church-blue-800 active:scale-98 text-white font-bold text-xs sm:text-base transition-all duration-200 shadow-md hover:shadow-lg flex items-center justify-center gap-2 cursor-pointer disabled:opacity-60"
                    >
                      {status === "loading" ? (
                        <>
                          <span className="w-3.5 h-3.5 sm:w-4 sm:h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                          <span>Envoi en cours...</span>
                        </>
                      ) : (
                        <>
                          <Send className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                          <span>Envoyer</span>
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
